import {
  Grid,
  Modal,
  Text,
  Fieldset,
  Flexbox,
  Input,
  Spacer,
  Spinner,
  toast,
  CustomDatePicker,
  Note,
  Icon,
} from '@components/common'
import { Controller, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { DepRunInput, depUndoSchema } from '@api/models'
import { yupResolver } from '@hookform/resolvers/yup'
import useDepUndoHook from './useDepUndoHook'
import DepreciationCard from './DepreciationCard'

interface DepreciationUndoFormProps {
  assetCompanyID: number
  isPeriodBased: boolean
}

const DepreciationUndoForm = ({
  assetCompanyID,
  isPeriodBased,
}: DepreciationUndoFormProps) => {
  const {
    isLoadingGetDepUndoInfo,
    isSuccessGetDepUndoInfo,
    isDepUndoFormOpen,
    isDepreciationUndoing,
    isErrorDepUndo,
    // isSuccessDepUndo,
    isErrorGetDepUndoInfo,
    errorGetDepUndoInfo,
    depUndoInfo,
    fields,
    openDepUndoForm,
    closeDepUndoForm,
    undoDepreciation,
  } = useDepUndoHook(assetCompanyID)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<DepRunInput>({
    mode: 'onSubmit',
    resolver: yupResolver(depUndoSchema(isPeriodBased)),
  })

  useEffect(() => {
    if (isSuccessGetDepUndoInfo) {
      reset(depUndoInfo)
    } else if (isErrorGetDepUndoInfo) {
      toast.error(errorGetDepUndoInfo)
    }
  }, [
    isSuccessGetDepUndoInfo,
    isErrorGetDepUndoInfo,
    depUndoInfo,
    errorGetDepUndoInfo,
    reset,
  ])

  useEffect(() => {
    if (isErrorDepUndo) {
      toast.error('Period Undo failed')
    }
  }, [isErrorDepUndo])

  function getInputProps(name: keyof DepRunInput) {
    return {
      ...register(name),
      error: errors[name] !== undefined,
      errorMsg: errors[name]?.message,
      disabled: isSubmitting,
    }
  }
  function getDateProps(field: any) {
    return {
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
      select: field.value,
      onBlur: field.onBlur,
      error: errors[field] !== undefined,
      errorMsg: errors[field]?.message,
    }
  }

  const isUndoDisabled = assetCompanyID === undefined || assetCompanyID === null
  const formErrorMsg = errors['']?.message || ''
  const shouldShowDepUndoForm = isSuccessGetDepUndoInfo && isDepUndoFormOpen

  function closeDepFormUndo() {
    reset(depUndoInfo)
    closeDepUndoForm()
  }

  return (
    <>
      <Modal
        isOpen={isDepreciationUndoing}
        close={() => {}}
        title="Undoing Period Depreciation"
      >
        <Flexbox justifyContent="center">
          <Spinner size="large" />
        </Flexbox>
      </Modal>

      <Modal
        isOpen={shouldShowDepUndoForm}
        close={closeDepFormUndo}
        onSubmit={handleSubmit(undoDepreciation)}
        title="Period Depreciation Undo"
        actionLabel="Undo"
      >
        {shouldShowDepUndoForm && (
          <Grid>
            {formErrorMsg &&
              errors['PostDate'] === undefined &&
              errors['AsOfDate'] === undefined && (
                <Grid.Item>
                  <Note intent="danger" content={<>{formErrorMsg}</>} />
                </Grid.Item>
              )}
            <Grid.Item>
              <Fieldset legend="Status" legendCenter>
                <Flexbox>
                  <Grid>
                    <Grid.Item m={4}>
                      <Input
                        label={fields.CM_COM_ID}
                        value={depUndoInfo.AssetCompanyID}
                        readOnly
                      />
                    </Grid.Item>
                    <Grid.Item m={4}>
                      <Input
                        label={fields.CM_DESCS}
                        value={depUndoInfo.ShortName}
                        readOnly
                      />
                    </Grid.Item>
                    <Grid.Item m={4}>
                      <Input
                        label="Last Year Closed"
                        value={depUndoInfo.LastYearClosed}
                        readOnly
                      />
                    </Grid.Item>
                  </Grid>
                </Flexbox>
              </Fieldset>
            </Grid.Item>
            <Grid.Item>
              <Fieldset legend="Parameters" legendCenter>
                <Flexbox>
                  <Grid>
                    {isPeriodBased ? (
                      <>
                        <Grid.Item m={4}>
                          <Input {...getInputProps('FiscalYear')} label="Year" />
                        </Grid.Item>
                        <Grid.Item m={3}>
                          <Input {...getInputProps('Period')} label="Period" />
                        </Grid.Item>
                      </>
                    ) : (
                      <Grid.Item m={5}>
                        <Controller
                          control={control}
                          name="AsOfDate"
                          render={({ field }) => (
                            <CustomDatePicker
                              {...getDateProps(field)}
                              label="As of Date"
                              error={errors['AsOfDate'] !== undefined}
                              errorMsg={errors['AsOfDate']?.message}
                            />
                          )}
                        />
                      </Grid.Item>
                    )}
                    <Grid.Item m={5}>
                      <Controller
                        control={control}
                        name="PostDate"
                        render={({ field }) => (
                          <CustomDatePicker
                            {...getDateProps(field)}
                            label="Posting Date"
                            error={errors['PostDate'] !== undefined}
                            errorMsg={errors['PostDate']?.message}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Flexbox>
              </Fieldset>
            </Grid.Item>
            <Grid.Item>
              <Fieldset legend="Remarks" legendCenter>
                <Grid>
                  <Grid.Item>
                    <Flexbox>
                      <Text bold>1.</Text>
                      <Spacer x="compact" />
                      <Input {...getInputProps('Remark1')} label="" />
                    </Flexbox>
                  </Grid.Item>
                  <Grid.Item>
                    <Flexbox>
                      <Text bold>2.</Text>
                      <Spacer x="compact" />
                      <Input {...getInputProps('Remark2')} label="" />
                    </Flexbox>
                  </Grid.Item>
                  <Grid.Item>
                    <Flexbox>
                      <Text bold>3.</Text>
                      <Spacer x="compact" />
                      <Input {...getInputProps('Remark3')} label="" />
                    </Flexbox>
                  </Grid.Item>
                </Grid>
              </Fieldset>
            </Grid.Item>
          </Grid>
        )}
      </Modal>

      <DepreciationCard
        disabled={isUndoDisabled}
        loading={isLoadingGetDepUndoInfo}
        openForm={openDepUndoForm}
        helpText="Permanently remove depreciation results from the history for a specific period."
        icon={<Icon name="RotateCcw" color="var(--primary-color500)" />}
        type="Undo"
      />
    </>
  )
}

export default DepreciationUndoForm
